"use client"

import { CREATOR_LEVELS } from "@/lib/community/types"
import { Sprout, Wrench, Zap, Brain, Flame, Crown, type LucideIcon } from "lucide-react"

const LEVEL_ICONS: Record<string, LucideIcon> = {
  seedling: Sprout,
  wrench: Wrench,
  zap: Zap,
  brain: Brain,
  flame: Flame,
  crown: Crown,
}

/**
 * CreatorLevelBadge — 6 escalating visual tiers, no emoji.
 *
 * Lv1: muted chip (plain)
 * Lv2: colored chip with subtle border
 * Lv3: colored chip with glow border + shimmer
 * Lv4: gradient bg + soft glow
 * Lv5: gradient + animated glow + shimmer sweep
 * Lv6: gradient + rainbow border + breathing glow (legendary)
 */
export function CreatorLevelBadge({
  level,
  showTitle = true,
  size = "sm",
  className = "",
}: {
  level: 1 | 2 | 3 | 4 | 5 | 6
  showTitle?: boolean
  size?: "sm" | "md"
  className?: string
}) {
  const info = CREATOR_LEVELS[level - 1]
  const Icon = LEVEL_ICONS[info.icon] ?? Sprout
  const isMd = size === "md"
  const iconSize = isMd ? "w-3.5 h-3.5" : "w-3 h-3"

  /* ── Lv1: plain muted chip ── */
  if (level === 1) {
    return (
      <span className={`inline-flex items-center gap-1 ${isMd ? "px-2.5 py-1 text-xs" : "px-2 py-0.5 text-caption"} rounded-full bg-muted/60 text-muted-foreground font-medium ${className}`}>
        <Icon className={iconSize} />
        {showTitle && <span>{info.title}</span>}
      </span>
    )
  }

  /* ── Lv2: colored chip with border ── */
  if (level === 2) {
    return (
      <span className={`inline-flex items-center gap-1 ${isMd ? "px-2.5 py-1 text-xs" : "px-2 py-0.5 text-caption"} rounded-full ${info.bgClass} ${info.textClass} font-medium border border-blue-500/20 ${className}`}>
        <Icon className={iconSize} />
        {showTitle && <span>{info.title}</span>}
      </span>
    )
  }

  /* ── Lv3: colored chip with glow border + shimmer ── */
  if (level === 3) {
    return (
      <span className={`creator-badge-shimmer inline-flex items-center gap-1 ${isMd ? "px-2.5 py-1 text-xs" : "px-2 py-0.5 text-caption"} rounded-full ${info.bgClass} ${info.textClass} font-medium border border-primary/30 ${className}`}>
        <Icon className={iconSize} />
        {showTitle && <span className="relative z-1">{info.title}</span>}
      </span>
    )
  }

  /* ── Lv4: gradient bg + soft outer glow ── */
  if (level === 4) {
    return (
      <span
        className={`creator-badge-glow inline-flex items-center gap-1 ${isMd ? "px-2.5 py-1 text-xs" : "px-2 py-0.5 text-caption"} rounded-full font-semibold text-white ${className}`}
        style={{
          background: `linear-gradient(135deg, ${info.color}, ${info.colorEnd})`,
          ["--badge-glow-color" as string]: "rgba(245,178,50,0.35)",
        }}
      >
        <Icon className={iconSize} />
        {showTitle && <span>{info.title}</span>}
      </span>
    )
  }

  /* ── Lv5: gradient + animated glow + shimmer sweep ── */
  if (level === 5) {
    return (
      <span
        className={`creator-badge-glow creator-badge-shimmer inline-flex items-center gap-1 ${isMd ? "px-2.5 py-1 text-xs" : "px-2 py-0.5 text-caption"} rounded-full font-semibold text-white ${className}`}
        style={{
          background: `linear-gradient(135deg, ${info.color}, ${info.colorEnd})`,
          ["--badge-glow-color" as string]: "rgba(245,100,60,0.4)",
        }}
      >
        <Icon className={`${iconSize} relative z-1`} />
        {showTitle && <span className="relative z-1">{info.title}</span>}
      </span>
    )
  }

  /* ── Lv6: legendary — gradient + rainbow border + breathing glow ── */
  return (
    <span
      className={`creator-badge-legendary inline-flex items-center gap-1 ${isMd ? "px-2.5 py-1 text-xs" : "px-2 py-0.5 text-caption"} rounded-full font-bold text-white ${className}`}
      style={{
        background: `linear-gradient(135deg, ${info.color}, ${info.colorEnd})`,
        ["--badge-glow-color" as string]: "rgba(245,180,60,0.45)",
      }}
    >
      <Icon className={`${iconSize} relative z-1`} />
      {showTitle && <span className="relative z-1">{info.title}</span>}
    </span>
  )
}
